import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { toast } from 'react-hot-toast'

// Set up axios interceptors
axios.interceptors.request.use(
  (config) => {
    // Get token from Zustand store directly
    const authStorage = localStorage.getItem('auth-storage')
    let token = null
    
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage)
        token = parsed.state?.token || null
      } catch (e) {
        console.error('Error parsing auth storage:', e)
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Read the persisted auth state directly from storage (interceptors run at
// module scope, before the store instance is available on first load).
const readPersistedAuth = (): { token?: string | null; refreshToken?: string | null; isAuthenticated?: boolean } | null => {
  try {
    const raw = localStorage.getItem('auth-storage')
    if (!raw) return null
    return JSON.parse(raw)?.state || null
  } catch (e) {
    console.error('Error parsing auth storage:', e)
    return null
  }
}

// Force a clean logout and send the user to the login screen. Guarded so that
// a burst of concurrent 401s only triggers a single redirect/toast.
let isLoggingOut = false
const forceLogout = (message?: string) => {
  if (isLoggingOut) return
  isLoggingOut = true

  toast.dismiss()
  if (message) toast.error(message)

  try {
    useAuthStore.getState().logout()
  } catch (e) {
    console.error('Error during forced logout:', e)
  }

  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// Coordinate a single in-flight refresh so concurrent 401s don't each fire
// their own refresh request.
let isRefreshing = false
let refreshSubscribers: Array<(token: string | null) => void> = []
const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
  refreshSubscribers.push(cb)
}
const notifyTokenRefreshed = (token: string | null) => {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

// Response interceptor: transparently refresh an expired token once, then
// retry the original request. If refresh isn't possible, log out automatically.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Skip interceptor processing for calls that explicitly request silent error handling
    if (error.config?.skipErrorHandler) {
      return Promise.reject(error)
    }

    const originalRequest = error.config || {}
    const status = error.response?.status
    const url: string = originalRequest.url || ''
    const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/refresh')

    if (status !== 401 || isAuthEndpoint || originalRequest._retry) {
      // A 401 from the refresh endpoint itself means the session is truly dead.
      if (status === 401 && url.includes('/api/auth/refresh')) {
        forceLogout('Your session has expired. Please log in again.')
      }
      return Promise.reject(error)
    }

    const auth = readPersistedAuth()
    if (!auth?.token || !auth?.isAuthenticated || !auth?.refreshToken) {
      // Authenticated request but no way to recover the session.
      forceLogout('Your session has expired. Please log in again.')
      return Promise.reject(error)
    }

    originalRequest._retry = true
    originalRequest.headers = originalRequest.headers || {}

    // A refresh is already in progress; queue this request until it resolves.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken) => {
          if (!newToken) {
            reject(error)
            return
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          resolve(axios(originalRequest))
        })
      })
    }

    isRefreshing = true
    try {
      const refreshed = await useAuthStore.getState().refreshAuth()
      isRefreshing = false

      if (refreshed) {
        const newToken = useAuthStore.getState().token
        notifyTokenRefreshed(newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return axios(originalRequest)
      }

      notifyTokenRefreshed(null)
      forceLogout('Your session has expired. Please log in again.')
      return Promise.reject(error)
    } catch (refreshError) {
      isRefreshing = false
      notifyTokenRefreshed(null)
      forceLogout('Your session has expired. Please log in again.')
      return Promise.reject(error)
    }
  }
)

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  companyName?: string
  roles: string[]
}

export interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  isSessionExpiredModalOpen: boolean
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  refreshAuth: () => Promise<boolean>
  clearError: () => void
  setUser: (user: User) => void
  showSessionExpiredModal: () => void
  hideSessionExpiredModal: () => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isSessionExpiredModalOpen: false,

      // Actions
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await axios.post('/api/auth/login', {
            email,
            password
          })

          if (response.data.success) {
            const { user, token, refreshToken } = response.data
            
            set({
              user,
              token,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })

            // Token is now automatically added by axios interceptor
            return true
          } else {
            set({
              isLoading: false,
              error: response.data.error || 'Login failed'
            })
            return false
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Login failed'
          set({
            isLoading: false,
            error: errorMessage
          })
          return false
        }
      },

      logout: () => {
        // Clear all toasts before logout
        toast.dismiss()
        
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        })
        
        // Token is now automatically handled by axios interceptor
      },

      refreshAuth: async () => {
        const { refreshToken } = get()
        
        if (!refreshToken) {
          return false
        }

        try {
          const response = await axios.post('/api/auth/refresh', {
            refreshToken
          })

          if (response.data.success) {
            const { token, refreshToken: newRefreshToken } = response.data
            
            set({
              token,
              refreshToken: newRefreshToken
            })

            return true
          }
        } catch (error) {
          // Refresh failed, logout user
          get().logout()
        }

        return false
      },

      clearError: () => {
        set({ error: null })
      },

      setUser: (user: User) => {
        set({ user })
      },

      showSessionExpiredModal: () => {
        set({ isSessionExpiredModalOpen: true })
      },

      hideSessionExpiredModal: () => {
        set({ isSessionExpiredModalOpen: false })
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)
